<template>
  <el-col :span="24">
    <el-form v-model="form" :label-position="labelPosition" :label-width="labelWidth">
      <el-form-item :label="field.label" v-for="(field, index) in fields" :key="index" style="margin-bottom: 12px;" v-if="!field.hidden">
        <el-input
          class="keynav"
          v-if="field && field.fieldType === 'input' && !field.autocomplete"
          v-model="form[field.name]"
          :tabindex="field.index"
          :ref="`${idPrefix}${index}`"
          :id="idPrefix + index"
          :name="field.name"
          :disabled="field.read_only"
          @blur="$emit('fieldFocusChanged', field.name)"
          @change="change(field.name)"
        />
        <MoneyField
          v-model="form[field.name]"
          class="keynav money-field text-right"
          v-if="form && field.fieldType === 'money'"
          :id="idPrefix + (index + 1)"
          :ref="`${idPrefix}${index}`"
          :name="field.name"
          :disabled="field.read_only"
          @blur="() => change(field.name)"
          @change="() => change(field.name)"
        />
        <el-select
          class="keynav"
          v-else-if="form[field.name] && field.fieldType === 'select' && form[field.name].hasOwnProperty('value')"
          v-model="form[field.name]"
          :tabindex="field.index"
          :ref="`${idPrefix}${index}`"
          :id="idPrefix + index"
          :name="field.name"
          :filterable="field.filterable"
          :multiple="field.multiple"
          :loading="null"
          :placeholder="field.filterable ? 'Search' : 'Select option'"
          :disabled="field.read_only"
          @change="$emit('selectMenuChanged', field.name)">
          <el-option
            v-for="item in field.choices"
            :key="item.value"
            :label="item.display_name"
            :value="item.value"
          />
        </el-select>
        <el-select
          class="keynav"
          v-else-if="form[field.name] && field.fieldType === 'select' && !form[field.name].hasOwnProperty('value')"
          v-model="form[field.name]"
          :tabindex="field.index"
          :ref="`${idPrefix}${index}`"
          :id="idPrefix + index"
          :name="field.name"
          :filterable="field.filterable"
          :multiple="field.multiple"
          :loading="null"
          :placeholder="field.filterable ? 'Search' : 'Select option'"
          :disabled="field.read_only"
          @change="$emit('selectMenuChanged', field.name)">
          <el-option
            v-for="item in field.choices"
            :key="item.value"
            :label="item.display_name"
            :value="item.value"
          />
        </el-select>
        <el-checkbox
          class="keynav form-checkbox"
          :tabindex="field.index"
          :ref="`${idPrefix}${index}`"
          :id="idPrefix + index"
          :name="field.name"
          :disabled="field.read_only"
          v-else-if="field.fieldType === 'checkbox'"
          v-model="form[field.name]"
          @change="change(field.name)"
        />
        <DateField
          class="keynav"
          :id="`${idPrefix}${index}`"
          :name="field.name"
          :disabled="field.read_only"
          :ref="`${idPrefix}${index}`"
          :dataoptions="{ 'data-type': 'date' }"
          v-model="form[field.name]"
          @blur="$emit('selectMenuChanged', field.name)"
          v-else-if="field.fieldType === 'date'"
        />
      </el-form-item>
    </el-form>
  </el-col>
</template>

<script>
  import validFormFields from '@/utils/validElementFormInputs'
  import { navigateHeader } from '@/utils/helpers/NavigateForm'
  import filterInvalidDOMElements from '@/utils/helpers/FilterInvalidDOMElements'
  import { hideOpenSuggestionMenus, shouldDisableArrowNav, handleTabKey } from '@/utils/helpers/DataEntryViews/ArrowNavHelper'
  import MoneyField from '@/components/InputComponents/MoneyField.vue'
  import DateField from '@/components/InputComponents/DateField.vue'

  export default {
    components: {
      MoneyField,
      DateField
    },
    props: {
      fields: Array,
      form: Object,                               // When multiple instances of this component are loaded in the DOM, it is necessary to
      idPrefix: String,                           // <--- pass an id prefix in order to prevent duplicate ids in the DOM. Whatever prefix you pass,
      formChanged: Boolean,
      labelPosition: String,
      labelWidth: String,
      modalOpen: Boolean
    },
    data () {
      return {
        loading: false,
        rows: []
      }
    },
    methods: {
      change (fieldName) {
        this.$emit('formChanged', { field: fieldName, value: this.form[fieldName] })
      },
      getFieldName () {
        return document.activeElement.name
      },
      handleKeydown (e) {
        if (this.modalOpen) {
          e.preventDefault()
          return
        }
        const fieldName = this.getFieldName()
        if ([37, 38, 39, 40, 13].includes(e.keyCode)) {
          this.navigateForm(e)
        } else if (e.keyCode === 9) {
          handleTabKey(this)
        } else {
          let field = this.form[fieldName]
          if (field && field.hasOwnProperty('value')) field.value = ''
        }
        this.checkFormField(fieldName)
      },
      checkFormField (fieldName) {
        const field = this.form[fieldName]
        if (field.hasOwnProperty('value')) {
          if (!field.value) {
            document.getElementsByName(fieldName)[0].style.backgroundColor = '#ff49493d'                    // Set color to indicate that value has been set
          } else {
            document.getElementsByName(fieldName)[0].style.backgroundColor = '#fff'                         // Set color to indicate that value has been set
          }
        }
      },
      navigateForm (e) {                                                                                    // Enable form navigation with arrow keys (and some others)
        if (shouldDisableArrowNav(this, e)) {                                                               // --- Determine if arrow key nav should be disabled, which is userful if the active element is an
          return false                                                                                      //     autocomplete or select that is displaying a dropdown,  with suggestions. In such a case,
        }                                                                                                   //     the down arrow key should not navigate to the next element, but rather allow the user to browse suggestions.
        hideOpenSuggestionMenus(this)                                                                       // --- Hide any open suggestion menus before navigating to next field
        const id = e.srcElement.closest('.keynav').className.includes('el-select')                          // --- See ** below
          ? e.srcElement.closest('.el-input').id                                                            //
          : e.srcElement.closest('.keynav').id                                                              //
        const idParts = id.split('-')                                                                       // --- Each form element has a unique id consisting of a prefix letter and a number. This separates the id
        const idPrefix = idParts[0]                                                                         //     into an array containing the prefix at index 0...
        const idNum = parseInt(idParts[1])                                                                  //     and the number at index 1
        const targetId = navigateHeader(e, idNum, (Math.round(this.fields.length / 2)), 2)                  // --- This gets the id of the element the user is trying to navigate to (targetId), which depends on the key
        const elementsFoundById = document.getElementById(`${idPrefix}-${targetId}`)
        if (elementsFoundById) {
          let elements = elementsFoundById.getElementsByTagName('*')                                        // --- Once we have the target id, we can select the element it's assigned to, and (because of Element-UI)....
          let validElements = filterInvalidDOMElements(elements, validFormFields)                           //     filter children that *aren't* input elements (<input>, <select>, <checkbox>, etc.)...
          return validElements[0].focus()                                                                   //     then we can finally focus on the input element.
        }
      }
    }
  }
</script>

<style>
  td input {
    border: none !important;
    height: 22px !important;
  }
  td textarea {
    border: none !important;
    height: 22px !important;
  }
  .money-field {
    border-radius: 4px;
    border: 1px solid #bfcbd9;
    box-sizing: border-box;
    height: 36px;
    line-height: 1;
    outline: 0;
    padding: 3px 10px;
    transition: border-color .2s cubic-bezier(.645,.045,.355,1);
    width: 100% !important;
  }
</style>

<style scoped>
  /*Table style properties*/
  table {
    width: 100%;
    margin-bottom: 1em;
    border-collapse: collapse;
  }
  td {
    padding: 0.2em;
    border: 1px solid #ccc;
    background-color: #ffffff;
    font-size: 12px;
  }
  td:first-child {
    text-align: right;
    background-color: #efefef;
    padding: 3px;
  }
  td:nth-child(2) {
    text-align: left;
  }
  td:nth-child(3) {
    text-align: right;
    background-color: #efefef;
    padding: 3px;
  }
  td:nth-child(4) {
    text-align: left;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
  .form-checkbox {
    padding: 0 10px;
  }
  .el-select {
    width: 100%;
  }
</style>



// WEBPACK FOOTER //
// src/components/Programs/BankRecon/BankRecForm.vue